const Nav = () => {
  const body = document.querySelector('body')
  const nav = document.getElementById('nav')
  const navButton = document.getElementById('show-menu')

  const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

  // ── Helpers ─────────────────────────────────────────────────────────────

  function getSubmenuLinks(li) {
    const sub = li.querySelector('.sub-menu')
    return sub ? Array.from(sub.querySelectorAll('a')) : []
  }

  function getParentLinkText(li) {
    const link = li.querySelector(':scope > .menu-item-inner > a')
    return link ? link.textContent.trim() : ''
  }

  function lockSubmenuLinks(li) {
    getSubmenuLinks(li).forEach((a) => {
      a.setAttribute('tabindex', '-1')
    })
  }

  function unlockSubmenuLinks(li) {
    getSubmenuLinks(li).forEach((a) => {
      a.removeAttribute('tabindex')
    })
  }

  // ── Submenu toggle ──────────────────────────────────────────────────────

  function openSubmenu(li, focusFirst) {
    const btn = li.querySelector('.show-children')
    const subMenu = li.querySelector('.sub-menu')
    if (!btn) return
    const title = getParentLinkText(li)
    li.setAttribute('data-expanded', 'true')
    btn.classList.add('active')
    btn.setAttribute('aria-expanded', 'true')
    btn.setAttribute('aria-label', `Hide ${title} submenu`)
    if (subMenu) subMenu.removeAttribute('aria-hidden')
    unlockSubmenuLinks(li)
    if (focusFirst) {
      const firstLink = li.querySelector('.sub-menu a')
      if (firstLink) firstLink.focus()
    }
  }

  function closeSubmenu(li) {
    const btn = li.querySelector('.show-children')
    const subMenu = li.querySelector('.sub-menu')
    if (!btn) return
    const title = getParentLinkText(li)
    li.removeAttribute('data-expanded')
    btn.classList.remove('active')
    btn.setAttribute('aria-expanded', 'false')
    btn.setAttribute('aria-label', `Show ${title} submenu`)
    if (subMenu) subMenu.setAttribute('aria-hidden', 'true')
    lockSubmenuLinks(li)
  }

  function toggleSubmenu(li, focusFirst) {
    if (li.getAttribute('data-expanded') === 'true') {
      closeSubmenu(li)
    } else {
      closeAllSubmenusExcept(li)
      openSubmenu(li, focusFirst)
    }
  }

  function closeAllSubmenusExcept(exceptLi) {
    Array.prototype.forEach.call(
      nav.querySelectorAll('#menu-main > li[data-expanded]'),
      (li) => {
        if (li !== exceptLi) closeSubmenu(li)
      },
    )
  }

  function closeAllSubmenus() {
    Array.prototype.forEach.call(
      nav.querySelectorAll('#menu-main > li[data-expanded]'),
      closeSubmenu,
    )
  }

  // ── Initialise: lock all submenu links, set aria-hidden, wire up buttons ──

  Array.prototype.forEach.call(
    nav.querySelectorAll('#menu-main > li.menu-item-has-children'),
    (li) => {
      lockSubmenuLinks(li)

      const subMenu = li.querySelector('.sub-menu')
      if (subMenu) subMenu.setAttribute('aria-hidden', 'true')

      const btn = li.querySelector('.show-children')
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          toggleSubmenu(li, true)
        })
      }
    },
  )

  // ── Mobile menu open / close ────────────────────────────────────────────

  function openMobileMenu() {
    navButton.classList.add('active')
    nav.classList.add('active')
    body.classList.add('u-overflow-hidden')
    navButton.setAttribute('aria-expanded', 'true')
    navButton.setAttribute('aria-label', 'Close menu')
    const firstFocusable = nav.querySelector(FOCUSABLE)
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100)
    }
  }

  function closeMobileMenu() {
    navButton.classList.remove('active')
    nav.classList.remove('active')
    body.classList.remove('u-overflow-hidden')
    navButton.setAttribute('aria-expanded', 'false')
    navButton.setAttribute('aria-label', 'Open menu')
    closeAllSubmenus()
    navButton.focus()
  }

  navButton.addEventListener('click', (e) => {
    e.preventDefault()
    if (!navButton.classList.contains('active')) {
      openMobileMenu()
    } else {
      closeMobileMenu()
    }
  })

  // ── Keyboard: Escape, Arrow keys, Tab trap ──────────────────────────────

  nav.addEventListener('keydown', (e) => {
    const isMobileMenuOpen = nav.classList.contains('active')
    const activeEl = document.activeElement

    // Escape key
    if (e.key === 'Escape') {
      const submenuLi = activeEl.closest('#menu-main > li')

      // If inside a submenu, close it and return focus to toggle button
      if (submenuLi && submenuLi.getAttribute('data-expanded') === 'true') {
        e.preventDefault()
        const btn = submenuLi.querySelector('.show-children')
        closeSubmenu(submenuLi)
        if (btn) btn.focus()
        return
      }

      // If mobile menu is open, close it
      if (isMobileMenuOpen) {
        e.preventDefault()
        closeMobileMenu()
        return
      }
    }

    // Arrow key navigation
    if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      const currentSubmenuItem = activeEl.closest('.sub-menu')
      const currentTopItem = activeEl.closest('#menu-main > li')

      if (currentSubmenuItem && currentTopItem) {
        handleSubmenuArrows(e, currentTopItem)
      } else if (currentTopItem) {
        handleTopLevelArrows(e, currentTopItem)
      }
    }

    // Tab focus trap in mobile menu
    if (isMobileMenuOpen && e.key === 'Tab') {
      const focusables = nav.querySelectorAll(FOCUSABLE)
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (e.shiftKey && activeEl === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault()
        first.focus()
      }
    }
  })

  // Close open submenus when focus moves outside #menu-main
  document.addEventListener('focusin', (e) => {
    if (!e.target.closest('#menu-main')) {
      closeAllSubmenus()
    }
  })

  function handleTopLevelArrows(e, currentItem) {
    const items = Array.from(nav.querySelectorAll('#menu-main > li'))
    const index = items.indexOf(currentItem)
    const isExpanded = currentItem.getAttribute('data-expanded') === 'true'
    const submenu = currentItem.querySelector('.sub-menu')

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        if (currentItem.getAttribute('data-expanded') === 'true') {
          closeSubmenu(currentItem)
        }
        if (index < items.length - 1) {
          items[index + 1].querySelector('a').focus()
        }
        break
      case 'ArrowLeft':
        e.preventDefault()
        if (currentItem.getAttribute('data-expanded') === 'true') {
          closeSubmenu(currentItem)
        }
        if (index > 0) {
          items[index - 1].querySelector('a').focus()
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (isExpanded && submenu) {
          const firstLink = submenu.querySelector('a')
          if (firstLink) firstLink.focus()
        } else if (!isExpanded) {
          toggleSubmenu(currentItem, true)
        } else if (index < items.length - 1) {
          items[index + 1].querySelector('a').focus()
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index > 0) {
          items[index - 1].querySelector('a').focus()
        }
        break
      case 'Home':
        e.preventDefault()
        items[0].querySelector('a').focus()
        break
      case 'End':
        e.preventDefault()
        items[items.length - 1].querySelector('a').focus()
        break
    }
  }

  function handleSubmenuArrows(e, parentLi) {
    const submenu = parentLi.querySelector('.sub-menu')
    if (!submenu) return
    const links = Array.from(submenu.querySelectorAll('a'))
    const index = links.indexOf(document.activeElement)
    const toggleBtn = parentLi.querySelector('.show-children')

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (index < links.length - 1) {
          links[index + 1].focus()
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index > 0) {
          links[index - 1].focus()
        } else if (toggleBtn) {
          closeSubmenu(parentLi)
          toggleBtn.focus()
        }
        break
      case 'ArrowRight': {
        e.preventDefault()
        const items = Array.from(nav.querySelectorAll('#menu-main > li'))
        const parentIndex = items.indexOf(parentLi)
        if (parentIndex < items.length - 1) {
          closeSubmenu(parentLi)
          items[parentIndex + 1].querySelector('a').focus()
        }
        break
      }
      case 'ArrowLeft': {
        e.preventDefault()
        const items = Array.from(nav.querySelectorAll('#menu-main > li'))
        const parentIndex = items.indexOf(parentLi)
        if (parentIndex > 0) {
          closeSubmenu(parentLi)
          items[parentIndex - 1].querySelector('a').focus()
        }
        break
      }
      case 'Home':
        e.preventDefault()
        links[0].focus()
        break
      case 'End':
        e.preventDefault()
        links[links.length - 1].focus()
        break
    }
  }
}

export default Nav
